import qs from 'qs';
import { httpClient, qsOptions } from 'providers/clients/httpClient';
import { Facets } from 'utility/types/dataProvider';

// ------------------------------------------------
// BROADCAST/NOTIFIER TYPES
// ------------------------------------------------

// GET /broadcast/account/ parameters (none)
// GET /broadcast/account/ result (status, data)
export type BroadcastAccountItem = {
  id?: string;
  type?: 'account' | 'integration';
  provider?: string;
  user_name?: string;
  via?: string;
  source?: string | null;
  change?: 'added' | 'updated' | 'removed' | 'unchanged';
};

export type GetBroadcastAccountResult = {
  status?: string | number;
  data?: BroadcastAccountItem[];
};

// DELETE /broadcast/account/{id}/ parameters
export type DeleteBroadcastAccountIdParams = {
  path: {
    id: string;
  };
};

// DELETE /broadcast/account/{id}/ result (204 No Content)
export type DeleteBroadcastAccountIdResult = {
  status?: string | number;
  [key: string]: any;
};

/**
 * GET /broadcast/target/ parameters
 * note: renaming 'driver_name[]' - this is a string[]
 * api will expect qp eg: ?driver_name[]=d1&driver_name[]=d2
 * using 'driver_name' as the key for
 * for easier code usage / stringify serialization
 */
export type GetBroadcastTargetParams = {
  query?: {
    driver_name?: string[];
    page?: string | number;
    limit?: number;
    sort?: 'id' | 'driver' | 'name';
    dir?: 'asc' | 'ASC' | 'desc' | 'DESC';
  };
};

export type BroadcastTargetItem = {
  id: string;
  driver: string;
  name?: string | undefined;
};

// GET /broadcast/target/ result
export type GetBroadcastTargetResult = {
  status?: string | number;
  data?: {
    total?: string | number;
    next?: string | number;
    prev?: string | number;
    facets?: Facets;
    items?: BroadcastTargetItem[];
  };
};

// GET /broadcast/provider/ parameters QP (search, filters, pagination, etc)
type GetBroadcastProviderQueryParams = {
  query?: {
    page?: string | number;
    limit?: number;
    sort?: 'name' | 'title';
    dir?: 'asc' | 'ASC' | 'desc' | 'DESC';
  };
};

export type BroadcastProviderItem = {
  name: string;
  title?: string;
};

// GET /broadcast/provider/ result
export type GetBroadcastProviderResult = {
  status?: string | number;
  data?: {
    total?: string | number;
    next?: string | number;
    prev?: string | number;
    facets?: Facets;
    items?: BroadcastProviderItem[];
  };
};

type TSendStatus =
  | 'cancelled'
  | 'draft'
  | 'failed'
  | 'pending'
  | 'sent'
  | 'staged'
  | 'undelivered'
  | 'incomplete';

type GetBroadcastMessageQPs = {
  query?: string;
  'send_status[]'?: TSendStatus;
  'channels[]'?: string[];
  send_date?: {
    min?: string | null;
    max?: string | null;
  };
  asset_uuid?: string;
  asset_app?: string;
  page?: number;
  limit?: number;
  sort?:
    | 'title'
    | 'send_date'
    | 'asset_app'
    | 'id'
    | 'site_id'
    | 'message'
    | 'social_title'
    | 'social_summary'
    | 'asset_id'
    | 'url'
    | 'send_status'
    | 'type';
  dir?: 'asc' | 'ASC' | 'desc' | 'DESC';
};

/**
 * GET /broadcast/message/ parameters
 * eg search broadcast messages, or related to specifc asset
 * note: renaming various bracket type accessors.
 *
 * NOTE: api will still expect eg: ?channels[]=c1&channels[]=c2
 */

export type GetBroadcastMessageParams = {
  query?: {
    query?: GetBroadcastMessageQPs['query'];
    send_status?: GetBroadcastMessageQPs['send_status[]'];
    channels?: GetBroadcastMessageQPs['channels[]'];
    send_date?: GetBroadcastMessageQPs['send_date'];
    asset_uuid?: GetBroadcastMessageQPs['asset_uuid'];
    asset_app?: GetBroadcastMessageQPs['asset_app'];
    page?: GetBroadcastMessageQPs['page'];
    limit?: GetBroadcastMessageQPs['limit'];
    sort?: GetBroadcastMessageQPs['sort'];
    dir?: GetBroadcastMessageQPs['dir'];
  };
};

export type BroadcastMessageItem = {
  message_id?: string;
  asset_uuid?: string | null;
  asset_app?: string | null;
  domain?: string;
  target?: string | null;
  title?: string;
  message?: string;
  social_title?: string;
  social_summary?: string;
  send_date?: string | null;
  channels?: {
    id?: string;
    name?: string;
    disabled?: boolean;
    publish_rules?: string[];
    targets?: {
      id?: string;
      driver?: string;
      name?: string;
    }[];
  }[];
  send_status?: TSendStatus;
  type?: 'manual' | 'auto';
};

// GET /broadcast/message/ result
export type GetBroadcastMessageResult = {
  status?: string | number;
  data?: {
    total?: string | number;
    next?: string | number;
    prev?: string | number;
    facets?: Facets;
    items?: BroadcastMessageItem[];
  };
};

type RawPostBroadcastMessageBody = {
  asset_uuid?: string | null;
  asset_app?: string | null;
  target?: string | null;
  title?: string;
  message: string;
  social_title?: string;
  social_summary?: string;
  send_date?: string | null;
  channels?: string[];
};
// POST /broadcast/message/ params payload
export type PostBroadcastMessageParams = {
  body: RawPostBroadcastMessageBody;
};

// POST /broadcast/message/ result (201 Created)
export type PostBroadcastMessageResult = {
  status?: string | number;
  data?: BroadcastMessageItem;
};

// GET /broadcast/message/{id}/
export type GetBroadcastMessageIdParams = {
  path: {
    id: string;
  };
};

// GET /broadcast/message/{id}/ result
export type GetBroadcastMessageIdResult = {
  status?: string | number;
  data?: BroadcastMessageItem;
};

// PUT /broadcast/message/{id}/ path parameters
type PutBroadcastMessageIdPathParams = {
  id: string;
};

// PUT /broadcast/message/{id}/ body payload
type PutBroadcastMessageIdBody = {
  asset_uuid?: string | null;
  asset_app?: string | null;
  target?: string | null;
  title?: string | null;
  message?: string | null;
  social_title?: string | null;
  social_summary?: string | null;
  send_date?: string | null;
  channels?: string[];
};

// FULL PUT /broadcast/message/{id}/ parameters (path + body)
export type PutBroadcastMessageIdParams = {
  path: PutBroadcastMessageIdPathParams;
  body: PutBroadcastMessageIdBody;
};

// PUT /broadcast/message/{id}/ result (200 OK, status, data)
export type PutBroadcastMessageIdResult = {
  status?: string | number;
  data?: BroadcastMessageItem;
};
// DELETE /broadcast/message/{id}/ parameters
export type DeleteBroadcastMessageIdParams = {
  path: {
    id: string;
  };
};

// DELETE /broadcast/message/{id}/ result (204 No Content)
export type DeleteBroadcastMessageIdResult = {
  [key: string]: any;
  status?: string | number;
};
// POST /broadcast/message/{id}/publish/ parameters
export type PostBroadcastMessageIdPublishParams = {
  path: {
    id: string;
  };
};
// POST /broadcast/message/{id}/publish/ result (200 OK, status, data)
export type PostBroadcastMessageIdPublishResult = {
  status?: string | number;
  data?: BroadcastMessageItem;
};
// GET /broadcast/message/default/message/ parameters
export type GetBroadcastMessageDefaultMessageParams = {
  query?: {
    asset_uuid?: string;
    asset_app?: string;
  };
};
// GET /broadcast/message/default/message/ result
export type GetBroadcastMessageDefaultMessageResult = {
  status?: string | number;
  data?: {
    start_time?: string;
    title?: string;
    message: string;
    social_title?: string;
    social_summary?: string;
  };
};

// GET /broadcast/settings/ (no get params currently)

type BroadcastSettingsResultItem = {
  publish?: {
    auto_publish?: boolean;
    asset_max_age?: number;
  };
};

// GET /broadcast/settings/ result
export type GetBroadcastSettingsResult = {
  status?: string | number;
  data?: BroadcastSettingsResultItem;
};

// PUT /broadcast/settings/ payload
type RawPutBroadcastSettingsBody = {
  publish?: {
    auto_publish?: boolean;
    asset_max_age?: number;
  };
};

// FULL PUT parameters (body only, no path params)
export type PutBroadcastSettingsParams = {
  body: RawPutBroadcastSettingsBody;
};

// PUT /broadcast/settings/ result (200 OK, status, data)
export type PutBroadcastSettingsResult = {
  status?: string | number;
  data?: BroadcastSettingsResultItem;
};
// GET /broadcast/channel/ parameters (search, filters, pagination, etc.)
export type GetBroadcastChannelParams = {
  query?: {
    message_id?: string;
    disabled?: boolean;
    page?: string | number;
    limit?: number;
    sort?: 'id' | 'site_id' | 'name';
    dir?: 'asc' | 'ASC' | 'desc' | 'DESC';
  };
};

export type BroadcastChannelItem = {
  id: string;
  name: string;
  disabled: boolean;
  targets?: {
    id: string;
    driver: string;
    name?: string;
  }[];
  publish_rules?: {
    id?: string;
    channel_id?: string;
    name?: string;
    type: 'assetType' | 'flags' | 'keywords' | 'sections';
    values: string[];
  }[];
};

// GET /broadcast/channel/ result
export type GetBroadcastChannelResult = {
  status?: string | number;
  data?: {
    total?: string | number;
    next?: string | number;
    prev?: string | number;
    facets?: Facets;
    items?: BroadcastChannelItem[];
  };
};

// POST /broadcast/channel/ body payload
type PostBroadcastChannelBody = {
  name: string;
  disabled?: boolean;
  targets: {
    id?: string;
    driver?: string;
  }[];
  publish_rules?: {
    name?: string;
    type?: 'assetType' | 'flags' | 'keywords' | 'sections';
    values?: string[];
  }[];
};

// FULL POST /broadcast/channel/ parameters (body only)
export type PostBroadcastChannelParams = {
  body: PostBroadcastChannelBody;
};

type BroadcastChannelResultItem = {
  id?: string;
  name?: string;
  disabled?: boolean;
  targets?: {
    id?: string;
    driver?: string;
    name?: string | undefined;
  }[];
  publish_rules?: {
    id?: string | undefined;
    channel_id?: string | undefined;
    name?: string | undefined;
    type: 'assetType' | 'flags' | 'keywords' | 'sections';
    values: string[];
  }[];
};

// POST /broadcast/channel/ result (201 Created, status, data)
export type PostBroadcastChannelResult = {
  status?: string | number;
  data?: BroadcastChannelResultItem;
};

// GET /broadcast/channel/{id}/ parameters
export type GetBroadcastChannelIdParams = {
  path: {
    id: string;
  };
};

// GET /broadcast/channel/{id}/ result
export type GetBroadcastChannelIdResult = {
  status?: string | number;
  data?: BroadcastChannelResultItem;
};

// PUT /broadcast/channel/{id}/ path parameters
type PutBroadcastChannelIdPathParams = {
  id: string;
};
// PUT /broadcast/channel/{id}/ body payload
type PutBroadcastChannelIdBody = {
  name?: string;
  disabled?: boolean;
  targets?: {
    id?: string;
    driver?: string;
  }[];
  publish_rules?: {
    name?: string;
    type?: 'assetType' | 'flags' | 'keywords' | 'sections';
    values?: string[];
  }[];
};
// FULL PUT parameters /broadcast/channel/{id}/ (path + body)
export type PutBroadcastChannelIdParams = {
  path: PutBroadcastChannelIdPathParams;
  body: PutBroadcastChannelIdBody;
};

// PUT /broadcast/channel/{id}/ result (200 OK, status, data)
export type PutBroadcastChannelIdResult = {
  status?: string | undefined;
  data?: BroadcastChannelResultItem;
};
// DELETE /broadcast/channel/{id}/ parameters
export type DeleteBroadcastChannelIdParams = {
  path: {
    id: string;
  };
};

// DELETE /broadcast/channel/{id}/ result (204 No Content)
export type DeleteBroadcastChannelIdResult = {
  status?: string | number;
  [key: string]: any;
};

// -----------------------------------------------
// ------  BROADCAST/NOTIFIER PROVIDER OBJ -------
// -----------------------------------------------

export const broadcastProvider = {
  // GET /broadcast/account/
  getBroadcastAccount: async (): Promise<GetBroadcastAccountResult> => {
    try {
      const url = `/broadcast/account/`;

      const { data } = await httpClient<GetBroadcastAccountResult>({
        url: url,
        method: 'GET',
      });

      const result: GetBroadcastAccountResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBroadcastAccount] Error:', error);
      throw error;
    }
  },
  // DELETE /broadcast/account/{id}/
  deleteBroadcastAccountId: async (
    params: DeleteBroadcastAccountIdParams,
  ): Promise<DeleteBroadcastAccountIdResult> => {
    try {
      const accountId = params?.path?.id;

      if (!accountId) {
        throw new Error('[deleteBroadcastAccountId] Missing required parameters.');
      }

      const url = `/broadcast/account/${accountId}/`;

      const { data, status } = await httpClient({
        url: url,
        method: 'DELETE',
      });

      // Success is 204 No Content
      const result: DeleteBroadcastAccountIdResult = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteBroadcastAccountId] Error:', error);
      throw error;
    }
  },
  // GET /broadcast/target/
  getBroadcastTarget: async (
    params: GetBroadcastTargetParams,
  ): Promise<GetBroadcastTargetResult> => {
    try {
      const basePath = '/broadcast/target/';

      const qp = params?.query ?? {};

      const payload = {
        ...(qp?.page && { page: qp.page }),
        ...(qp?.dir && { dir: qp.dir }),
        ...(qp?.limit && { limit: qp.limit }),
        ...(qp?.sort && { sort: qp.sort }),
        ...(qp?.driver_name && { driver_name: qp.driver_name }),
      };

      // prepend qps if at least one key in payload
      const hasQp = Object.keys(payload).length > 0;
      const qpStr = hasQp ? qs.stringify(payload, qsOptions) : '';
      const finalUrl = qpStr ? `${basePath}?${qpStr}` : basePath;

      const { data } = await httpClient<GetBroadcastTargetResult>({
        url: finalUrl,
        method: 'GET',
      });

      const result: GetBroadcastTargetResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBroadcastTarget] Error:', error);
      throw error;
    }
  },
  // GET /broadcast/provider/
  getBroadcastProvider: async (
    params: GetBroadcastProviderQueryParams,
  ): Promise<GetBroadcastProviderResult> => {
    try {
      const basePath = '/broadcast/provider/';

      const qp = params?.query ?? {};

      const payload = {
        ...(qp?.page && { page: qp.page }),
        ...(qp?.dir && { dir: qp.dir }),
        ...(qp?.limit && { limit: qp.limit }),
        ...(qp?.sort && { sort: qp.sort }),
      };

      const hasQp = Object.keys(payload).length > 0;
      const qpStr = hasQp ? qs.stringify(payload, qsOptions) : '';
      const finalUrl = qpStr ? `${basePath}?${qpStr}` : basePath;

      const { data } = await httpClient<GetBroadcastProviderResult>({
        url: finalUrl,
        method: 'GET',
      });

      const result: GetBroadcastProviderResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBroadcastProvider] Error:', error);
      throw error;
    }
  },
  // GET /broadcast/message/
  getBroadcastMessage: async (
    params: GetBroadcastMessageParams,
  ): Promise<GetBroadcastMessageResult> => {
    try {
      const basePath = '/broadcast/message/';

      const qp = params?.query ?? {};

      const payload = {
        ...(qp?.query && { query: qp.query }),
        ...(qp?.send_status && { send_status: qp.send_status }),
        ...(qp?.channels && { channels: qp.channels }),
        ...(qp?.send_date && { send_date: qp.send_date }),
        ...(qp?.asset_app && { asset_app: qp.asset_app }),
        ...(qp?.asset_uuid && { asset_uuid: qp.asset_uuid }),
        ...(qp?.page && { page: qp.page }),
        ...(qp?.limit && { limit: qp.limit }),
        ...(qp?.dir && { dir: qp.dir }),
        ...(qp?.sort && { sort: qp.sort }),
      };

      const hasQp = Object.keys(payload).length > 0;
      const qpStr = hasQp ? qs.stringify(payload, qsOptions) : '';
      const finalUrl = qpStr ? `${basePath}?${qpStr}` : basePath;

      const { data } = await httpClient<GetBroadcastMessageResult>({
        url: finalUrl,
        method: 'GET',
      });

      const result: GetBroadcastMessageResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBroadcastMessage] Error:', error);
      throw error;
    }
  },
  // POST /broadcast/message/
  postBroadcastMessage: async (
    params: PostBroadcastMessageParams,
  ): Promise<PostBroadcastMessageResult> => {
    try {
      const body = params?.body;

      if (!body) {
        throw new Error('[postBroadcastMessage] Missing required parameters.');
      }

      const url = `/broadcast/message/`;

      const { data } = await httpClient<PostBroadcastMessageResult>({
        url: url,
        method: 'POST',
        data: JSON.stringify(body),
      });

      const result: PostBroadcastMessageResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[postBroadcastMessage] Error:', error);
      throw error;
    }
  },
  // GET /broadcast/message/{id}/
  getBroadcastMessageId: async (
    params: GetBroadcastMessageIdParams,
  ): Promise<GetBroadcastMessageIdResult> => {
    try {
      const messageId = params?.path?.id;

      if (!messageId) {
        throw new Error('[getBroadcastMessageId] Missing required parameters.');
      }

      const url = `/broadcast/message/${messageId}/`;

      const { data } = await httpClient<GetBroadcastMessageIdResult>({
        url: url,
        method: 'GET',
      });

      const result: GetBroadcastMessageIdResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBroadcastMessageId] Error:', error);
      throw error;
    }
  },
  // PUT /broadcast/message/{id}/
  putBroadcastMessageId: async (
    params: PutBroadcastMessageIdParams,
  ): Promise<PutBroadcastMessageIdResult> => {
    try {
      const messageId = params?.path?.id;
      const body = params?.body;

      if (!messageId || !body) {
        throw new Error('[putBroadcastMessageId] Missing required parameters.');
      }

      const url = `/broadcast/message/${messageId}/`;

      const { data } = await httpClient<PutBroadcastMessageIdResult>({
        url: url,
        method: 'PUT',
        data: JSON.stringify(body),
      });

      const result: PutBroadcastMessageIdResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[putBroadcastMessageId] Error:', error);
      throw error;
    }
  },
  // DELETE /broadcast/message/{id}/
  deleteBroadcastMessageId: async (
    params: DeleteBroadcastMessageIdParams,
  ): Promise<DeleteBroadcastMessageIdResult> => {
    try {
      const messageId = params?.path?.id;

      if (!messageId) {
        throw new Error('[deleteBroadcastMessageId] Missing required parameters.');
      }

      const url = `/broadcast/message/${messageId}/`;

      const { data, status } = await httpClient({
        url: url,
        method: 'DELETE',
      });

      // Success is 204 No Content
      const result: DeleteBroadcastMessageIdResult = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteBroadcastMessageId] Error:', error);
      throw error;
    }
  },
  // POST PUBLISH /broadcast/message/{id}/publish/
  postBroadcastMessageIdPublish: async (
    params: PostBroadcastMessageIdPublishParams,
  ): Promise<PostBroadcastMessageIdPublishResult> => {
    try {
      const messageId = params?.path?.id;

      if (!messageId) {
        throw new Error('[postBroadcastMessageIdPublish] Missing required parameters.');
      }

      const url = `/broadcast/message/${messageId}/publish/`;

      const { data } = await httpClient<PostBroadcastMessageIdPublishResult>({
        url: url,
        method: 'POST',
      });

      const result: PostBroadcastMessageIdPublishResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[postBroadcastMessageIdPublish] Error:', error);
      throw error;
    }
  },
  // GET /broadcast/message/default/message/
  getBroadcastMessageDefaultMessage: async (
    params: GetBroadcastMessageDefaultMessageParams,
  ): Promise<GetBroadcastMessageDefaultMessageResult> => {
    try {
      const url = `/broadcast/message/default/message/`;

      const { data } = await httpClient<GetBroadcastMessageDefaultMessageResult>({
        url: url,
        method: 'GET',
      });

      const result: GetBroadcastMessageDefaultMessageResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBroadcastMessageDefaultMessage] Error:', error);
      throw error;
    }
  },
  // GET /broadcast/settings/
  getBroadcastSettings: async (): Promise<GetBroadcastSettingsResult> => {
    try {
      const url = `/broadcast/settings/`;

      const { data } = await httpClient<GetBroadcastSettingsResult>({
        url: url,
        method: 'GET',
      });

      const result: GetBroadcastSettingsResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBroadcastSettings] Error:', error);
      throw error;
    }
  },
  // PUT /broadcast/settings/
  putBroadcastSettings: async (
    params: PutBroadcastSettingsParams,
  ): Promise<PutBroadcastSettingsResult> => {
    try {
      const body = params?.body;

      if (!body) {
        throw new Error('[putBroadcastSettings] Missing required parameters.');
      }

      const url = `/broadcast/settings/`;

      const { data } = await httpClient<PutBroadcastSettingsResult>({
        url: url,
        method: 'PUT',
        data: JSON.stringify(body),
      });

      const result: PutBroadcastSettingsResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[putBroadcastSettings] Error:', error);
      throw error;
    }
  },
  // GET /broadcast/channel/
  getBroadcastChannel: async (
    params: GetBroadcastChannelParams,
  ): Promise<GetBroadcastChannelResult> => {
    try {
      const basePath = '/broadcast/channel/';

      const qp = params?.query ?? {};

      const payload = {
        ...(qp?.message_id && { message_id: qp.message_id }),
        ...(qp?.disabled && { disabled: qp.disabled }),
        ...(qp?.page && { page: qp.page }),
        ...(qp?.dir && { dir: qp.dir }),
        ...(qp?.limit && { limit: qp.limit }),
        ...(qp?.sort && { sort: qp.sort }),
      };

      // prepend qps if at least one key in payload
      const hasQp = Object.keys(payload).length > 0;
      const qpStr = hasQp ? qs.stringify(payload, qsOptions) : '';
      const finalUrl = qpStr ? `${basePath}?${qpStr}` : basePath;

      const { data } = await httpClient<GetBroadcastChannelResult>({
        url: finalUrl,
        method: 'GET',
      });

      const result: GetBroadcastChannelResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBroadcastChannel] Error:', error);
      throw error;
    }
  },
  // POST /broadcast/channel/
  postBroadcastChannel: async (
    params: PostBroadcastChannelParams,
  ): Promise<PostBroadcastChannelResult> => {
    try {
      const body = params?.body;

      if (!body) {
        throw new Error('[postBroadcastChannel] Missing required parameters.');
      }

      const url = `/broadcast/channel/`;

      const { data } = await httpClient<PostBroadcastChannelResult>({
        url: url,
        method: 'POST',
        data: JSON.stringify(body),
      });

      const result: PostBroadcastChannelResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[postBroadcastChannel] Error:', error);
      throw error;
    }
  },
  // GET /broadcast/channel/{id}/
  getBroadcastChannelId: async (
    params: GetBroadcastChannelIdParams,
  ): Promise<GetBroadcastChannelIdResult> => {
    try {
      const channelId = params?.path?.id;

      if (!channelId) {
        throw new Error('[getBroadcastChannelId] Missing required parameters.');
      }

      const url = `/broadcast/channel/${channelId}/`;

      const { data } = await httpClient<GetBroadcastChannelIdResult>({
        url: url,
        method: 'GET',
      });

      const result: GetBroadcastChannelIdResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getBroadcastChannelId] Error:', error);
      throw error;
    }
  },
  // PUT /broadcast/channel/{id}/
  putBroadcastChannelId: async (
    params: PutBroadcastChannelIdParams,
  ): Promise<PutBroadcastChannelIdResult> => {
    try {
      const channelId = params?.path?.id;
      const body = params?.body;

      if (!channelId || !body) {
        throw new Error('[putBroadcastChannelId] Missing required parameters.');
      }

      const url = `/broadcast/channel/${channelId}/`;

      const { data } = await httpClient<PutBroadcastChannelIdResult>({
        url: url,
        method: 'PUT',
        data: JSON.stringify(body),
      });

      const result: PutBroadcastChannelIdResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[putBroadcastChannelId] Error:', error);
      throw error;
    }
  },
  // DELETE /broadcast/channel/{id}/
  deleteBroadcastChannelId: async (
    params: DeleteBroadcastChannelIdParams,
  ): Promise<DeleteBroadcastChannelIdResult> => {
    try {
      const channelId = params?.path?.id;

      if (!channelId) {
        throw new Error('[deleteBroadcastChannelId] Missing required parameters.');
      }

      const url = `/broadcast/channel/${channelId}/`;

      const { data, status } = await httpClient({
        url: url,
        method: 'DELETE',
      });

      // Success is 204 No Content
      const result: DeleteBroadcastChannelIdResult = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteBroadcastChannelId] Error:', error);
      throw error;
    }
  },
  // set as const - all keys (functions) in this object
  // are readonly and we should to ensure they
  // are immutable / cant be accidentally modified
} as const;

// Since all functions have their own signature with types
// we can safely use typeof helper to create the type for us
// and then '&' add it to the BloxCustomDataProvider type
export type BroadcastProviderType = typeof broadcastProvider;
